import { Component, OnInit, Input, OnDestroy, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { PaginationEvent } from '../models/pagination-event.model';

@Component({
    selector: 'eui-table-paginator',
    templateUrl: './eui-table-paginator.component.html',
})

export class EuiTablePaginatorComponent implements OnInit, OnDestroy, OnChanges {

    @Input() pageSizeOptions: number[] = [5, 10, 25, 50, 100];
    @Input() pageSize = 10;
    @Input() page = 0;
    @Input() length: number;
    @Input()
    get hasPageNumberNavigation() { return this._hasPageNumberNavigation; }
    set hasPageNumberNavigation(value) { this._hasPageNumberNavigation = coerceBooleanProperty(value); }
    _hasPageNumberNavigation = false;
    @Input() nbPageNumberNavigation: number = 5;

    @Output() pageChange = new EventEmitter<PaginationEvent>();

    public page$: BehaviorSubject<PaginationEvent> = new BehaviorSubject(null);
    public length$: BehaviorSubject<number> = new BehaviorSubject(0);
    public range: string;
    public nbPage: number;

    private nbPageNumberNav: number;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor() {}

    ngOnChanges(c: SimpleChanges): void {
        if (c && c.length) {
            this.length$.next(c.length.currentValue);
            this.getRange();
        }
    }

    ngOnInit(): void {
        if (this.length) {
            this.length$.next(this.length);
        }

        this.nbPageNumberNav = this.nbPageNumberNavigation;

        this.length$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((length) => {
            this.length = length;
            this.nbPage = Math.ceil(this.length / this.pageSize);
            this.page = 0;
            this.getRange();

            if (this.nbPage < this.nbPageNumberNavigation) {
                this.nbPageNumberNav = this.nbPage;
            } else {
                this.nbPageNumberNav = this.nbPageNumberNavigation;
            }

            this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        });
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    get pageNumberNavigation(): number[] {
        const pages = [];
        for (let i = 0; i < this.nbPage; i ++) {
            pages.push(i);
        }

        const delta = Math.floor(this.nbPageNumberNav / 2);
        let start = 0;
        if (this.page - delta > 0) {
            start = this.page - delta;
        } else {
            start = 0;
        }

        if (this.nbPage - this.page <= this.nbPageNumberNav - (delta + 1)) {
            start = this.nbPage - this.nbPageNumberNav;
        }

        const end = start + this.nbPageNumberNav;
        return pages.slice(start, end);
    }

    public changePageSize(size: number): void {
        this.pageSize = size;
        this.nbPage = Math.ceil(this.length / this.pageSize);
        this.page = 0;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public goToPage(page: number): void {
        this.page = page;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public goFirstPage(): void {
        this.page = 0;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public goPreviousPage(): void {
        this.page --;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public goNextPage(): void {
        this.page ++;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public goLastPage(): void {
        this.page = this.nbPage - 1;
        this.page$.next(new PaginationEvent({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage }));
        this.getRange();
        this.pageChange.emit({ page: this.page, pageSize: this.pageSize, nbPage: this.nbPage });
    }

    public setPage(page: PaginationEvent): void {
        this.page$.next(page);
    }

    public setLength(length: number): void {
        this.length$.next(length);
    }

    public getRange(): void {
        setTimeout(() => {
            const from = this.length > 0 ? (this.page * this.pageSize) + 1 : 0;
            const to = ((this.page * this.pageSize) + this.pageSize) <= this.length ?
                ((this.page * this.pageSize) + this.pageSize) :
                this.length;

            this.range = from && to && this.length ? from + ' - ' + to + ' of ' + this.length : '';
        });
    }

}
